// 特殊效果處理器
// Special Effect Handlers - 升級、胡牌、額外召喚等

import type { GameState, Card } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('SpecialHandlers');

/**
 * 升級效果處理器
 * 勇者公會核心機制
 */
export class UpgradeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('升級') || text.includes('升級為');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const upgradeToMatch = effectText.match(/使其升級為此卡/);
    if (upgradeToMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'upgradeToThis',
      });
    }

    const upgradeCondMatch = effectText.match(/可使該?([^升]+)升級為此卡/);
    if (upgradeCondMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'upgradeFromUnit',
        value: upgradeCondMatch[1].trim(),
      });
    }

    if (effectText.includes('你場上單位升級時')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onUpgrade',
      });
    }

    const upgradeEffectMatch = effectText.match(/你場上單位升級時[，,]召喚(\d+)個小旅人/);
    if (upgradeEffectMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        customHandler: 'onUpgradeSummon',
        targetSelector: {
          zone: 'extra',
          side: 'self',
          filter: { name: '小旅人' },
          count: parseInt(upgradeEffectMatch[1], 10),
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    return state;
  }
}

/**
 * 數量限制效果處理器
 */
export class QuantityLimitHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('數量上限');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const limitMatch = effectText.match(/【\s*數量上限\s*:\s*(\d+)\s*】/);
    if (limitMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'quantityLimit',
        value: parseInt(limitMatch[1], 10),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const value = action.value !== undefined ? String(action.value) : 'unknown';
    log.debug('Limit: ' + value);
    return state;
  }
}

/**
 * 額外區效果處理器
 */
export class ExtraZoneHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('額外區') || text.includes('從你額外區');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const summonExtraMatch = effectText.match(/使你額外區(\d*)張?「([^」]+)」被召喚/);
    if (summonExtraMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        customHandler: 'summonFromExtra',
        targetSelector: {
          zone: 'extra',
          side: 'self',
          filter: { name: summonExtraMatch[2] },
          count: summonExtraMatch[1] ? parseInt(summonExtraMatch[1], 10) : 1,
        },
      });
    }

    const searchExtraMatch = effectText.match(/從你額外區尋找(\d*)張?([^並]+)[^加]*加入手牌/);
    if (searchExtraMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchExtra',
        value: searchExtraMatch[2].trim(),
        targetSelector: {
          zone: 'extra',
          side: 'self',
          count: searchExtraMatch[1] ? parseInt(searchExtraMatch[1], 10) : 1,
        },
      });
    }

    const grantExtraMatch = effectText.match(/被賦予你額外區的?(\d*)張?「([^」]+)」/);
    if (grantExtraMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'grantFromExtra',
        value: grantExtraMatch[2],
        targetSelector: {
          zone: 'extra',
          side: 'self',
          count: grantExtraMatch[1] ? parseInt(grantExtraMatch[1], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const selector = action.targetSelector;
    if (!selector) return state;

    if (action.customHandler === 'summonFromExtra') {
      const cardName = selector.filter?.name;
      const count = selector.count ?? 1;
      const playerId = state.activePlayerId;
      const isPlayer1 = playerId === state.player1.id;
      const sideKey = isPlayer1 ? 'player1Side' : 'player2Side';

      const side = state.battlefield[sideKey];
      const emptySlots: { row: 'front' | 'back'; column: number }[] = [];

      side.frontLine.forEach((slot, idx) => {
        if (!slot.unit) emptySlots.push({ row: 'front', column: idx + 1 });
      });
      side.backLine.forEach((slot, idx) => {
        if (!slot.unit) emptySlots.push({ row: 'back', column: idx + 1 });
      });

      let newState = state;
      const toSummon = Math.min(count, emptySlots.length);

      for (let i = 0; i < toSummon; i++) {
        const slot = emptySlots[i];
        const newCard: Card = {
          id: 'extra-' + (cardName ?? 'unknown') + '-' + String(Date.now()) + '-' + String(i),
          definitionId: 'EXTRA-' + (cardName ?? 'unknown'),
          ownerId: playerId,
          location: 'battlefield',
          position: {
            side: isPlayer1 ? 'player1' : 'player2',
            row: slot.row,
            column: slot.column as 1 | 2 | 3 | 4 | 5,
          },
          orientation: 'upright',
          summonedThisTurn: true,
        };

        const lineKey = slot.row === 'front' ? 'frontLine' : 'backLine';
        const newBattlefield = { ...newState.battlefield };
        const newSide = { ...newBattlefield[sideKey] };
        const newLine = [...newSide[lineKey]];
        newLine[slot.column - 1] = { ...newLine[slot.column - 1], unit: newCard };
        newSide[lineKey] = newLine;
        newBattlefield[sideKey] = newSide;

        newState = { ...newState, battlefield: newBattlefield };
      }

      log.debug('Summoned ' + String(toSummon) + ' 「' + (cardName ?? 'unknown') + '」 from extra');
      return newState;
    }

    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    return state;
  }
}

/**
 * 胡牌效果處理器
 * 歡樂島核心機制
 */
export class HuCardHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('胡牌') || text.includes('紅中哥') || 
           text.includes('白板哥') || text.includes('發財哥') ||
           text.includes('東大哥') || text.includes('北小妹') ||
           text.includes('西小弟') || text.includes('南大姊');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('紅中哥') && effectText.includes('白板哥') && effectText.includes('發財哥')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'huDaSanYuan',
        value: 8,
      });
    }

    if (effectText.includes('東大哥') && effectText.includes('北小妹') && 
        effectText.includes('西小弟') && effectText.includes('南大姊')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'huDaSiXi',
        value: 8,
      });
    }

    const revealRewardMatch = effectText.match(/此卡被展示時[，,]獎勵\+(\d+)/);
    if (revealRewardMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onReveal',
        value: parseInt(revealRewardMatch[1], 10),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'huDaSanYuan' || action.customHandler === 'huDaSiXi') {
      const rewardValue = typeof action.value === 'number' ? action.value : 0;
      const playerId = state.activePlayerId;
      const isPlayer1 = playerId === state.player1.id;
      const playerKey = isPlayer1 ? 'player1' : 'player2';
      const player = state[playerKey];

      const currentReward = player.reward ?? 0;
      log.debug(action.customHandler + ' - 獎勵 +' + String(rewardValue));

      return {
        ...state,
        [playerKey]: {
          ...player,
          reward: currentReward + rewardValue,
        },
      };
    }

    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    return state;
  }
}

/**
 * 場地魔法效果處理器
 */
export class FieldMagicHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('場地魔法') || text.includes('場地牌');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('不得使用場地魔法卡')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'blockFieldMagic',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    log.debug(handler);
    return state;
  }
}

/**
 * 祭品效果處理器
 */
export class SacrificeHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('祭品') || text.includes('獻祭') || text.includes('無需祭品');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('不得被獻祭') || effectText.includes('無法被獻祭')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'cannotBeSacrificed',
      });
    }

    const sacrificeReqMatch = effectText.match(/此卡的祭品須為「([^」]+)」/);
    if (sacrificeReqMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'specificSacrifice',
        value: sacrificeReqMatch[1],
      });
    }

    const destroyNoSacMatch = effectText.match(/使敵方場上(\d*)個?無需祭品的單位被破壞/);
    if (destroyNoSacMatch) {
      actions.push({
        type: 'destroy',
        sourceCardId: '',
        customHandler: 'destroyNoSacrifice',
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
          count: destroyNoSacMatch[1] ? parseInt(destroyNoSacMatch[1], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    return state;
  }
}

/**
 * 道具效果處理器
 * 蒸汽世界機制
 */
export class ItemHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('道具') || text.includes('道具牌') || text.includes('道具單位');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    const searchItemMatch = effectText.match(/從你牌庫尋找(\d*)張?道具牌[^加]*加入手牌/);
    if (searchItemMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'searchItem',
        targetSelector: {
          zone: 'deck',
          side: 'self',
          filter: { type: 'unit' },
          count: searchItemMatch[1] ? parseInt(searchItemMatch[1], 10) : 1,
        },
      });
    }

    const itemChargeMatch = effectText.match(/你打出或召喚道具單位時[，,]充能\+(\d+)/);
    if (itemChargeMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'onItemSummon',
        value: parseInt(itemChargeMatch[1], 10),
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    const value = action.value !== undefined ? String(action.value) : '';
    log.debug(handler + ': ' + value);
    return state;
  }
}

/**
 * 商人效果處理器
 * 蒸汽世界機制
 */
export class MerchantHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('商人單位') || text.includes('商人');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('若你場上有商人單位')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'requireMerchant',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    log.debug(handler);
    return state;
  }
}

/**
 * 回合限制效果處理器
 */
export class TurnLimitHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('限一次') || text.includes('一回合限');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    if (effectText.includes('你的主要階段限一次')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'oncePerMainPhase',
      });
    }

    if (effectText.includes('一回合限一次')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'oncePerTurn',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const handler = action.customHandler ?? 'unknown';
    log.debug(handler);
    return state;
  }
}

/**
 * 條件類型定義
 */
export interface ConditionData {
  type: 'unitCount' | 'rewardMin' | 'scoreDiff' | 'enemyTapped' | 'factionCount';
  side?: 'self' | 'enemy';
  faction?: string;
  count?: number;
  value?: number;
}

/**
 * 條件觸發效果處理器
 * 支援「若...則...」格式的條件效果
 */
export class ConditionalHandler implements EffectHandler {
  canHandle(text: string): boolean {
    // 只處理包含「若...則」的條件效果
    return (text.includes('若') && text.includes('則')) || 
           text.includes('才可');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「若你場上有X個或以上的Y單位，則獎勵+Z」
    const unitCountRewardMatch = effectText.match(
      /若你場上有(\d+)個或以上的([^，,單位]+)單位[，,]則獎勵\+(\d+)/
    );
    if (unitCountRewardMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'conditionalEffect',
        condition: {
          type: 'unitCount',
          side: 'self',
          faction: unitCountRewardMatch[2].trim(),
          count: parseInt(unitCountRewardMatch[1], 10),
        } as ConditionData,
        consequent: [{
          type: 'custom',
          sourceCardId: '',
          customHandler: 'addReward',
          value: parseInt(unitCountRewardMatch[3], 10),
          targetSelector: { zone: 'battlefield', side: 'self' },
        }],
      });
      return actions;
    }

    // 解析「若你總分低於敵方X點或以上，則...」
    const scoreDiffMatch = effectText.match(
      /若你總分低於敵方(\d+)點或以上[，,]則(.+)/
    );
    if (scoreDiffMatch) {
      const consequence = this.parseConsequence(scoreDiffMatch[2]);
      if (consequence) {
        actions.push({
          type: 'custom',
          sourceCardId: '',
          customHandler: 'conditionalEffect',
          condition: {
            type: 'scoreDiff',
            value: parseInt(scoreDiffMatch[1], 10),
          } as ConditionData,
          consequent: [consequence],
        });
      }
      return actions;
    }

    // 解析「若你獎勵至少為X，則...」
    const rewardMinMatch = effectText.match(
      /若你獎勵至少為(\d+)[，,]則(.+)/
    );
    if (rewardMinMatch) {
      const consequence = this.parseConsequence(rewardMinMatch[2]);
      if (consequence) {
        actions.push({
          type: 'custom',
          sourceCardId: '',
          customHandler: 'conditionalEffect',
          condition: {
            type: 'rewardMin',
            value: parseInt(rewardMinMatch[1], 10),
          } as ConditionData,
          consequent: [consequence],
        });
      }
      return actions;
    }

    // 解析「若敵方場上至少有X個橫放單位，則...」
    const enemyTappedMatch = effectText.match(
      /若敵方場上至少有(\d+)個橫放單位[，,]則(.+)/
    );
    if (enemyTappedMatch) {
      const consequence = this.parseConsequence(enemyTappedMatch[2]);
      if (consequence) {
        actions.push({
          type: 'custom',
          sourceCardId: '',
          customHandler: 'conditionalEffect',
          condition: {
            type: 'enemyTapped',
            count: parseInt(enemyTappedMatch[1], 10),
          } as ConditionData,
          consequent: [consequence],
        });
      }
      return actions;
    }

    return actions;
  }

  /**
   * 解析結果子句（則...）
   */
  private parseConsequence(text: string): ImmediateAction | null {
    // 獎勵+X
    const rewardMatch = text.match(/獎勵\+(\d+)/);
    if (rewardMatch) {
      return {
        type: 'custom',
        sourceCardId: '',
        customHandler: 'addReward',
        value: parseInt(rewardMatch[1], 10),
        targetSelector: { zone: 'battlefield', side: 'self' },
      };
    }

    // 抽X張牌
    const drawMatch = text.match(/抽(\d+)張牌/);
    if (drawMatch) {
      return {
        type: 'draw',
        sourceCardId: '',
        value: parseInt(drawMatch[1], 10),
      };
    }

    // 使你場上所有單位+X攻擊力
    const buffMatch = text.match(/使你場上所有單位\+(\d+)攻擊力/);
    if (buffMatch) {
      return {
        type: 'buff',
        sourceCardId: '',
        value: parseInt(buffMatch[1], 10),
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
        },
      };
    }

    return null;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'conditionalEffect') {
      return state;
    }

    const condition = action.condition as ConditionData | undefined;
    const consequentActions = action.consequent;
    const consequent = consequentActions?.[0];

    if (!condition || !consequent) {
      log.debug('Missing condition or consequent');
      return state;
    }

    // 評估條件
    const conditionMet = this.evaluateCondition(state, condition);
    log.debug(`Condition ${condition.type}: ${String(conditionMet)}`);

    if (!conditionMet) {
      return state;
    }

    // 條件成立，但不直接執行 - 返回動作讓上層處理
    // (這裡簡化處理，直接執行基本效果)
    if (consequent.type === 'custom' && consequent.customHandler === 'addReward') {
      const value = typeof consequent.value === 'number' ? consequent.value : 0;
      const playerId = state.activePlayerId;
      const isPlayer1 = playerId === state.player1.id;
      const playerKey = isPlayer1 ? 'player1' : 'player2';
      const currentReward = state[playerKey].reward ?? 0;
      
      log.debug(`Adding reward +${String(value)}`);
      return {
        ...state,
        [playerKey]: {
          ...state[playerKey],
          reward: currentReward + value,
        },
      };
    }

    return state;
  }

  /**
   * 評估條件是否成立
   */
  private evaluateCondition(state: GameState, condition: ConditionData): boolean {
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;

    switch (condition.type) {
      case 'unitCount':
        return this.checkUnitCount(state, condition, isPlayer1);
      case 'rewardMin':
        return this.checkRewardMin(state, condition, isPlayer1);
      case 'scoreDiff':
        return this.checkScoreDiff(state, condition, isPlayer1);
      case 'enemyTapped':
        return this.checkEnemyTapped(state, condition, isPlayer1);
      default:
        return false;
    }
  }

  private checkUnitCount(
    state: GameState,
    condition: ConditionData,
    isPlayer1: boolean
  ): boolean {
    const sideKey = condition.side === 'enemy' 
      ? (isPlayer1 ? 'player2Side' : 'player1Side')
      : (isPlayer1 ? 'player1Side' : 'player2Side');
    
    const side = state.battlefield[sideKey];
    const faction = condition.faction?.toLowerCase();
    let count = 0;

    // 計算場上符合條件的單位數量
    const countInLine = (line: typeof side.frontLine): void => {
      for (const slot of line) {
        if (slot.unit) {
          // TODO: 需要 CardDefinition 來檢查種族
          // 目前簡化：如果沒指定種族，計算所有單位
          if (!faction || faction === '' || slot.unit.definitionId.includes(faction)) {
            count++;
          }
        }
      }
    };

    countInLine(side.frontLine);
    countInLine(side.backLine);

    return count >= (condition.count ?? 1);
  }

  private checkRewardMin(
    state: GameState,
    condition: ConditionData,
    isPlayer1: boolean
  ): boolean {
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const reward = state[playerKey].reward ?? 0;
    return reward >= (condition.value ?? 0);
  }

  private checkScoreDiff(
    state: GameState,
    condition: ConditionData,
    isPlayer1: boolean
  ): boolean {
    // 簡化：使用獎勵作為分數
    const myScore = isPlayer1 
      ? (state.player1.reward ?? 0) 
      : (state.player2.reward ?? 0);
    const enemyScore = isPlayer1 
      ? (state.player2.reward ?? 0) 
      : (state.player1.reward ?? 0);
    
    return (enemyScore - myScore) >= (condition.value ?? 0);
  }

  private checkEnemyTapped(
    state: GameState,
    condition: ConditionData,
    isPlayer1: boolean
  ): boolean {
    const sideKey = isPlayer1 ? 'player2Side' : 'player1Side';
    const side = state.battlefield[sideKey];
    let tappedCount = 0;

    const countTapped = (line: typeof side.frontLine): void => {
      for (const slot of line) {
        if (slot.unit?.orientation === 'tapped') {
          tappedCount++;
        }
      }
    };

    countTapped(side.frontLine);
    countTapped(side.backLine);

    return tappedCount >= (condition.count ?? 1);
  }
}

// 匯出所有特殊處理器
export const specialHandlers = {
  UpgradeHandler,
  QuantityLimitHandler,
  ExtraZoneHandler,
  HuCardHandler,
  FieldMagicHandler,
  SacrificeHandler,
  ItemHandler,
  MerchantHandler,
  TurnLimitHandler,
  ConditionalHandler,
};
